import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import ChartPanel from '../shared/ChartPanel';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from '../shared/chartTheme';
import { formatClinicDate } from '../../../utils/clinicTime';
import { roleLabel } from '../../attendance/attendanceStatus';
import { kpiMap } from './kpis';

// Who arrives late, and how often. One late morning is nothing; the same person late most days is
// what a manager actually wants to see, so this ranks by the number of days rather than by minutes,
// and marks the people for whom lateness is a habit.
//
// The share is of the days somebody came in, not of their working days. Being away is absence, which
// the Attendance Summary already counts, and mixing the two would let a week of leave improve
// somebody's punctuality.

// "Often late" has to mean something a reader can check: late on at least half the days they came
// in, and on at least three of them, so one bad week does not brand anybody.
const OFTEN_PERCENT = 50;
const OFTEN_DAYS = 3;
const oftenLate = (r) => r.daysLate >= OFTEN_DAYS && r.latePercent != null && r.latePercent >= OFTEN_PERCENT;

const VIEWS = [
  { key: 'late', label: 'Late at least once', keep: (r) => r.daysLate > 0 },
  { key: 'often', label: 'Often late', keep: oftenLate },
  { key: 'all', label: 'Everybody', keep: () => true },
];

const mins = (v) => (v == null || v === 0 ? '-' : `${Math.round(v)} min`);
const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

// A plain time of day with no date behind it, so none of the clinic-time helpers apply: they all
// take an instant. "09:14" reads back as the clock on the wall shows it.
const clockTime = (hhmm) => {
  if (!hhmm) return '-';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const COLUMNS = [
  {
    key: 'userName', label: 'Name', wrap: true,
    render: (r) => (
      <span className="flex items-center gap-2 flex-wrap">
        {r.userName}
        {oftenLate(r) && (
          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            Often late
          </span>
        )}
      </span>
    ),
    csv: (r) => r.userName,
    searchText: (r) => `${r.userName} ${oftenLate(r) ? 'often late' : ''}`,
  },
  { key: 'role', label: 'Role', render: (r) => roleLabel(r.role) },
  { key: 'workingDays', label: 'Working days', align: 'right' },
  { key: 'daysAttended', label: 'Days in', align: 'right' },
  {
    key: 'daysLate', label: 'Days late', align: 'right',
    render: (r) => <span className={r.daysLate > 0 ? 'font-semibold text-amber-700' : 'text-gray-400'}>{r.daysLate}</span>,
    csv: (r) => r.daysLate,
  },
  {
    key: 'latePercent', label: 'Share of days in', align: 'right',
    render: (r) => (
      <span className={`font-semibold ${oftenLate(r) ? 'text-red-700' : r.daysLate > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
        {pct(r.latePercent)}
      </span>
    ),
    csv: (r) => r.latePercent,
  },
  { key: 'averageLateMinutes', label: 'Average late', align: 'right', render: (r) => mins(r.averageLateMinutes), csv: (r) => r.averageLateMinutes },
  {
    key: 'worstLateMinutes', label: 'Worst', align: 'right', wrap: true,
    render: (r) => (r.worstLateMinutes ? `${mins(r.worstLateMinutes)} on ${formatClinicDate(r.worstDate, { day: 'numeric', month: 'short' })}` : '-'),
    csv: (r) => (r.worstLateMinutes ? `${r.worstLateMinutes} min on ${r.worstDate}` : ''),
    sortValue: (r) => r.worstLateMinutes || 0,
  },
  { key: 'typicalInTime', label: 'Usual arrival', align: 'right', render: (r) => clockTime(r.typicalInTime), csv: (r) => r.typicalInTime || '' },
];

const LateArrivalsReport = () => {
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [view, setView] = useState('late');

  const fetcher = useCallback(
    () => reportsAPI.getAttendanceLate({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const all = useMemo(() => data?.rows || [], [data]);
  const rows = useMemo(() => {
    const keep = VIEWS.find((v) => v.key === view)?.keep || (() => true);
    return all.filter(keep);
  }, [all, view]);

  // The ten worst, since a bar per member of staff would run off the bottom of the card.
  const chartRows = useMemo(
    () => all.filter((r) => r.daysLate > 0).slice(0, 10).map((r) => ({ name: r.userName, days: r.daysLate })),
    [all],
  );

  const k = kpiMap(data);
  const often = all.filter(oftenLate).length;
  const kpis = [
    { label: 'Late days', value: k.lateDays ?? all.reduce((s, r) => s + r.daysLate, 0), sub: 'Across all staff in the range' },
    { label: 'Late at least once', value: k.peopleLate ?? all.filter((r) => r.daysLate > 0).length, sub: 'Staff with a late day' },
    { label: 'Often late', value: often, sub: `${OFTEN_PERCENT}% of their days in, or more`, tone: often > 0 ? 'alert' : undefined },
    { label: 'Average lateness', value: k.averageLateMinutes == null ? '—' : `${k.averageLateMinutes} min`, sub: 'On the days somebody was late' },
    { label: 'Staff', value: k.staff ?? all.length, sub: 'With working days in the range' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Who to show">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            aria-pressed={view === v.key}
            onClick={() => setView(v.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === v.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Most late days first">
        {loading || chartRows.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">{loading ? 'Loading' : 'Nobody was late in the selected range'}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 34)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...axisProps} />
              <YAxis type="category" dataKey="name" {...axisProps} width={150} />
              <Tooltip {...tooltipStyle} formatter={(v) => [v, 'Late days']} />
              <Bar dataKey="days" radius={[0, 4, 4, 0]} barSize={16}>
                {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        title="Late arrivals per person"
        columns={COLUMNS}
        rows={rows}
        csvName={`late-arrivals-${range.from}-to-${range.to}`}
        emptyText={view === 'all' ? 'Nobody had working days in the selected range' : 'Nobody matches in the selected range'}
        footnote={`A day counts as late only once the grace period set under Attendance Settings has passed, and against that person's own start time for that day of the week. The share is of the days they came in, so leave and days off neither help nor hurt it. "Often late" means at least ${OFTEN_DAYS} late days and ${OFTEN_PERCENT}% or more of their days in.`}
      />
    </div>
  );
};

export default LateArrivalsReport;
